import { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Bell, 
  ChevronDown,
  ChevronRight,
  Menu, 
  Home,
} from 'lucide-react'
import { clsx } from 'clsx'
import { clientNavigation, vendorNavigation } from '../config/navigationConfig'
import { useAuth } from '../contexts/AuthContext'

// Helper Component for Recursive Dropdown Items
const NavItem = ({ item, depth = 0 }) => {
  const [isOpen, setIsOpen] = useState(false)
  const timeoutRef = useRef(null)
  const navigate = useNavigate()
  const containerRef = useRef(null)
  const menuRef = useRef(null)
  const [alignRight, setAlignRight] = useState(false)
  const [openLeft, setOpenLeft] = useState(false)
  const [openUp, setOpenUp] = useState(false)
  const hasChildren = item.children && item.children.length > 0
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsOpen(true)
  }
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 300)
  }
  const handleFocus = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setIsOpen(true)
  }
  const handleBlur = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 300)
  }
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])
  useEffect(() => {
    if (!isOpen) return
    const el = menuRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vw = window.innerWidth || document.documentElement.clientWidth
    const vh = window.innerHeight || document.documentElement.clientHeight
    if (depth === 0) {
      setAlignRight(rect.right > vw)
    } else {
      setOpenLeft(rect.right > vw)
    }
    setOpenUp(rect.bottom > vh)
  }, [isOpen, depth])

  if (hasChildren) {
    return (
      <div 
        className={clsx(
          "relative", 
          depth === 0 ? "h-full flex items-center" : "w-full"
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        ref={containerRef}
      >
        <button
          onClick={() => {
             if (hasChildren) setIsOpen(!isOpen)
             else navigate(item.href)
          }}
          aria-expanded={isOpen}
          aria-haspopup={hasChildren ? 'menu' : undefined}
          className={clsx(
            "flex items-center gap-2 transition-colors duration-200 outline-none",
            depth === 0 
              ? "px-2.5 py-1.5 text-[13px] font-medium rounded-md text-white hover:bg-gray-700 hover:text-white"
              : "w-full px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100 hover:text-blue-700 justify-between group",
            isOpen && depth === 0 ? "bg-gray-700 text-white" : "",
            isOpen && depth > 0 ? "bg-gray-100 text-blue-700" : ""
          )}
        >
          <div className="flex items-center gap-2.5">
            {/* Show Icon only for top level or if specifically requested */}
            {item.icon && depth === 0 && <item.icon className="w-4 h-4" />} 
            {item.icon && depth > 0 && <item.icon className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500" />}
            <span className={clsx(depth === 0 ? "whitespace-nowrap" : "truncate")}>{item.name}</span>
          </div>
          
          {/* Indicators */}
          {depth === 0 ? (
            <ChevronDown className={clsx("w-3 h-3 transition-transform duration-200", isOpen ? "rotate-180" : "")} />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          )}
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div 
            className={clsx(
              "absolute bg-white border border-gray-200 shadow-xl rounded-lg z-50 min-w-[240px] py-1 animate-in fade-in zoom-in-95 duration-100",
              depth === 0
                ? (alignRight
                    ? (openUp ? "bottom-full right-0 mb-1" : "top-full right-0 mt-1")
                    : (openUp ? "bottom-full left-0 mb-1" : "top-full left-0 mt-1"))
                : (openLeft 
                    ? (openUp ? "bottom-0 right-full mr-1" : "top-0 right-full mr-1")
                    : (openUp ? "bottom-0 left-full ml-1" : "top-0 left-full ml-1"))
            )}
            ref={menuRef}
          >
             {/* Header for Submenu if needed, or just list */}
             {item.children.map((child, idx) => (
               <NavItem key={`${child.name}-${idx}`} item={child} depth={depth + 1} />
             ))}
          </div>
        )}
      </div>
    )
  }

  // Leaf Node
  return (
    <Link
      to={item.href}
      className={clsx(
        "flex items-center gap-2.5 text-[13px] font-medium transition-colors duration-150 group",
        depth === 0 
          ? "font-medium text-white hover:bg-gray-700 hover:text-white px-2.5 py-1.5 rounded-md" 
          : "px-4 py-2.5 text-gray-700 hover:bg-gray-100 hover:text-blue-700"
      )}
    >
      {item.icon && <item.icon className={clsx("transition-colors", depth === 0 ? "w-4 h-4" : "w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500")} />}
      <span className="truncate">{item.name}</span>
    </Link>
  )
}

export default function TopNavigation({ onMobileMenuOpen, fullWidth = false }) {
  const { hasAnyPermission, activePortal, enabledModules, user } = useAuth()
  useNavigate()

  // Select navigation based on active portal context
  const activeNavigation = useMemo(() => {
    if (activePortal === 'vendor') {
      return vendorNavigation
    }
    return clientNavigation
  }, [activePortal])

  // Filter navigation based on permissions and enabled modules
  const filteredNavigation = useMemo(() => {
    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        const hasAccess = !item.permissions || item.permissions.length === 0 || hasAnyPermission(item.permissions)
        const isModuleEnabled = !item.module || (enabledModules && enabledModules.includes(item.module))
        
        if (item.children) {
          const filteredChildren = filterItems(item.children)
          // Keep parent if it has accessible children OR if it's a direct link itself
          if (filteredChildren.length > 0 && isModuleEnabled) {
            acc.push({ ...item, children: filteredChildren })
          } else if (hasAccess && isModuleEnabled && item.href && item.href !== '#') {
             acc.push({ ...item, children: [] })
          }
        } else {
          if (hasAccess && isModuleEnabled) {
            acc.push(item)
          }
        }
        return acc
      }, [])
    }
    const result = filterItems(activeNavigation)
    // Remove Dashboard from list since it's now the home icon
    return result.filter(item => item.name !== 'Dashboard')
  }, [hasAnyPermission, activeNavigation, enabledModules])

  return (
    <div className="flex flex-col z-50 relative">
      <div className={clsx('bg-[#1e293b] text-white h-14 flex items-center justify-between shrink-0 shadow-md', fullWidth ? 'px-2 sm:px-4 lg:px-6' : 'px-4')}>
        <div className="flex items-center gap-4 lg:gap-8 min-w-0">
          <button 
            onClick={onMobileMenuOpen}
            className="xl:hidden p-1 text-gray-300 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <Link to={activePortal === 'vendor' ? '/vendor/dashboard' : '/dashboard'} className="flex items-center gap-3 group min-w-fit">
            <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center shadow-lg group-hover:bg-blue-500 transition-colors ring-1 ring-blue-400/50">
              <Home className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-[10px] text-blue-300 font-bold uppercase tracking-widest leading-none">SANGLOB</span>
              <span className="text-base font-bold tracking-tight leading-none text-white">NXT ERP</span>
            </div>
          </Link>
          {/* Main Navigation - Horizontal items with hover dropdowns */}
          <nav className="hidden xl:flex items-center space-x-1 h-14 overflow-visible whitespace-nowrap pr-2" aria-label="Global Navigation">
            {filteredNavigation.map((item) => (
              <NavItem key={item.name} item={item} />
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button className="p-1.5 text-gray-400 hover:text-white rounded-full hover:bg-gray-700/50 transition-colors relative leading-none">
            <Bell className="w-4 h-4" />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-red-500 rounded-full border-2 border-[#1e293b]"></span>
          </button>
        </div>
      </div>
    </div>
  )
}
